import { apiRequest } from '@/lib/queryClient';
import { enqueueSync } from '@/lib/syncQueue';
import type { GameState, Team } from '@/lib/golfEngine';
import { normalizeIdentityName } from '@shared/identityNormalize';

const ROUND_ID_KEY = 'twoDownAutoPlus_roundId';

// ── Recovery-attempt idempotency key ────────────────────────────────────────
// Persisted to localStorage BEFORE the recovery POST is sent so that if the
// server commits but the response is lost (network timeout / page reload), the
// client can resend the same key and the server returns the existing round
// instead of creating a duplicate.  Cleared on success.
const RECOVERY_ATTEMPT_KEY = 'twoDownAutoPlus_recoveryAttemptId';

export function loadRecoveryAttemptId(): string | null {
  try { return localStorage.getItem(RECOVERY_ATTEMPT_KEY); } catch { return null; }
}

export function saveRecoveryAttemptId(id: string | null): void {
  try {
    if (id) localStorage.setItem(RECOVERY_ATTEMPT_KEY, id);
    else localStorage.removeItem(RECOVERY_ATTEMPT_KEY);
  } catch (e) {
    console.error('[roundSync] Failed to save recoveryAttemptId:', e);
  }
}

export function loadRoundId(): string | null {
  try {
    return localStorage.getItem(ROUND_ID_KEY);
  } catch {
    return null;
  }
}

export function saveRoundId(roundId: string | null): void {
  try {
    if (roundId) {
      localStorage.setItem(ROUND_ID_KEY, roundId);
    } else {
      localStorage.removeItem(ROUND_ID_KEY);
    }
  } catch (e) {
    console.error('[roundSync] Failed to save roundId:', e);
  }
}

const ROUND_OWNER_KEY = 'twoDownAutoPlus_ownerId';

export function loadRoundOwner(): string | null {
  try {
    return localStorage.getItem(ROUND_OWNER_KEY);
  } catch {
    return null;
  }
}

export function saveRoundOwner(userId: string | null): void {
  try {
    if (userId) {
      localStorage.setItem(ROUND_OWNER_KEY, userId);
    } else {
      localStorage.removeItem(ROUND_OWNER_KEY);
    }
  } catch (e) {
    console.error('[roundSync] Failed to save ownerId:', e);
  }
}

const ROUND_GROUP_KEY = 'twoDownAutoPlus_groupId';

export function loadRoundGroup(): string | null {
  try {
    return localStorage.getItem(ROUND_GROUP_KEY);
  } catch {
    return null;
  }
}

export function saveRoundGroup(groupId: string | null): void {
  try {
    if (groupId) {
      localStorage.setItem(ROUND_GROUP_KEY, groupId);
    } else {
      localStorage.removeItem(ROUND_GROUP_KEY);
    }
  } catch (e) {
    console.error('[roundSync] Failed to save groupId:', e);
  }
}

interface CreateRoundPayload {
  gameMode: string;
  matchFormat: string;
  courseName: string;
  courseDataId?: string;
  courseId?: string;
  courseSnapshotJson?: Record<string, any>;
  startingHole: 1 | 10;
  stakeFront: number;
  stakeBack: number;
  stakeOverall: number;
  teamsJson: Team[];
  players: Array<{
    displayName: string;
    team: 'A' | 'B';
    position: number;
    handicapIndex?: number;
    role: 'player' | 'organizer';
    personId?: string;
    phoneHash?: string;
    enginePlayerId?: string;
  }>;
}

export interface IdentityConflict {
  groupId: string;
  displayName: string;
  // Each entry is a group member whose normalized name matched the incoming
  // displayName. `claimedUserId` is non-null when the person has linked a
  // Clerk account, null for ghost/guest records.
  candidates: Array<{ personId: string; claimedUserId: string | null }>;
  playerPosition: number | null;
  playerTeam: 'A' | 'B' | null;
}

export interface AmbiguousIdentityResult {
  error: 'ambiguous_identity';
  message: string;
  // Aggregated across every player slot that hit an unresolvable name
  // collision in this submission, not just the first one found.
  conflicts: IdentityConflict[];
}

export type AuthRequiredResult = {
  error: 'auth';
  message: string;
};

export type CreateRoundResult =
  | { roundId: string; groupId: string | null }
  | AmbiguousIdentityResult
  | AuthRequiredResult
  | null;

export type OrganizerSlot = { team: 'A' | 'B'; position: number };

/**
 * Determines which of the (up to 4) player slots is "the organizer", in
 * priority order:
 *   1. Explicit isOrganizer flag — set at Setup, survives Throw Balls
 *      because the shuffle swaps whole player objects.
 *   2. Fallback for drafts persisted before isOrganizer existed: match the
 *      signed-in user's name against each slot's typed name. Only binds if
 *      there is EXACTLY ONE match — this is a narrow, deterministic
 *      comparison (not fuzzy matching), and multiple matches must still
 *      hard-block rather than guess.
 *   3. Last resort, fully backward compatible with pre-fix behavior:
 *      Team A position 0.
 *
 * Exported as a pure function (no fetch/localStorage) so it can be unit
 * tested directly — see tests/regression/spec3-organizer-slot-resolution.ts.
 */
export function resolveOrganizerSlot(
  teamAPlayers: Array<{ name: string; isOrganizer?: boolean }>,
  teamBPlayers: Array<{ name: string; isOrganizer?: boolean }>,
  organizerName?: string,
): OrganizerSlot {
  const allSlots: Array<OrganizerSlot & { name: string; isOrganizer?: boolean }> = [
    ...teamAPlayers.map((p, i) => ({ team: 'A' as const, position: i, name: p.name, isOrganizer: p.isOrganizer })),
    ...teamBPlayers.map((p, i) => ({ team: 'B' as const, position: i, name: p.name, isOrganizer: p.isOrganizer })),
  ];

  const flagged = allSlots.filter((s) => s.isOrganizer === true);
  if (flagged.length === 1) {
    return { team: flagged[0].team, position: flagged[0].position };
  }

  if (flagged.length === 0 && organizerName) {
    const normalizedOrganizerName = normalizeIdentityName(organizerName);
    if (normalizedOrganizerName.length > 0) {
      const nameMatches = allSlots.filter(
        (s) => normalizeIdentityName(s.name) === normalizedOrganizerName
      );
      if (nameMatches.length === 1) {
        return { team: nameMatches[0].team, position: nameMatches[0].position };
      }
      // 0 or 2+ matches: fall through to the slot-0 default below rather
      // than guess — hard-block stays in effect for genuine ambiguity.
    }
  }

  return { team: 'A', position: 0 };
}

export async function createRound(
  gameState: GameState,
  organizerPersonId?: string,
  organizerName?: string,
  recoveryAttemptId?: string,
): Promise<CreateRoundResult> {
  const teamAPlayers = gameState.teams[0]?.players ?? [];
  const teamBPlayers = gameState.teams[1]?.players ?? [];

  const organizerSlot = resolveOrganizerSlot(teamAPlayers, teamBPlayers, organizerName);

  const isOrganizerSlot = (team: 'A' | 'B', position: number) =>
    organizerSlot.team === team && organizerSlot.position === position;

  const players: CreateRoundPayload['players'] = [
    ...teamAPlayers.map((p, i): CreateRoundPayload['players'][number] => ({
      displayName: p.name,
      team: 'A',
      position: i,
      handicapIndex: p.handicap,
      role: isOrganizerSlot('A', i) ? 'organizer' : 'player',
      enginePlayerId: p.id,
      // Organizer slot: always use the session-sourced organizerPersonId (most
      // trusted — directly tied to the Clerk auth session).
      // Non-organizer slot: use the personId preserved from the chip-tap flow
      // if present, so the server can bind directly without name matching.
      // Absent for typed-name and first-appearance guest slots.
      ...(isOrganizerSlot('A', i) && organizerPersonId
        ? { personId: organizerPersonId }
        : p.personId
          ? { personId: p.personId }
          : {}),
    })),
    ...teamBPlayers.map((p, i): CreateRoundPayload['players'][number] => ({
      displayName: p.name,
      team: 'B',
      position: i,
      handicapIndex: p.handicap,
      role: isOrganizerSlot('B', i) ? 'organizer' : 'player',
      enginePlayerId: p.id,
      ...(isOrganizerSlot('B', i) && organizerPersonId
        ? { personId: organizerPersonId }
        : p.personId
          ? { personId: p.personId }
          : {}),
    })),
  ];

  const courseName = (gameState as any).courseData?.name ?? gameState.courseDataId ?? 'Unknown Course';

  const courseSnapshotJson = (gameState as any).courseSnapshot ?? undefined;
  const courseId = (gameState as any).courseId ?? undefined;

  const payload: CreateRoundPayload = {
    gameMode: gameState.gameMode,
    matchFormat: gameState.matchFormat,
    courseName,
    courseDataId: gameState.courseDataId,
    courseId,
    courseSnapshotJson,
    startingHole: gameState.startingHole,
    stakeFront: gameState.stakes.front,
    stakeBack: gameState.stakes.back,
    stakeOverall: gameState.stakes.overall,
    teamsJson: gameState.teams,
    players,
    ...(recoveryAttemptId ? { recoveryAttemptId } : {}),
  };

  try {
    // Use fetch directly (not apiRequest) so a 409 ambiguous-identity response
    // body can be read and returned as a distinguishable result instead of
    // being collapsed into a generic thrown Error by apiRequest's throwIfResNotOk.
    const res = await fetch('/api/rounds', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (res.status === 409) {
      const data = await res.json().catch(() => null);
      if (data?.error === 'ambiguous_identity') {
        return {
          error: 'ambiguous_identity',
          message: data.message ?? 'Multiple existing players match this name. Please choose or rename the player.',
          conflicts: Array.isArray(data.conflicts) ? data.conflicts : [],
        };
      }
    }

    if (res.status === 401) {
      console.error('[roundSync] Failed to create round: session expired (401)');
      return {
        error: 'auth',
        message: 'Your session expired. Please sign in again to save this round online.',
      };
    }

    if (!res.ok) {
      console.error('[roundSync] Failed to create round:', res.status, await res.text().catch(() => ''));
      return null;
    }

    const data = await res.json();
    const roundId = data.round?.id;
    const groupId = data.round?.groupId ?? null;
    if (roundId) {
      saveRoundId(roundId);
      return { roundId, groupId };
    }
    console.error('[roundSync] createRound response missing round.id:', data);
    return null;
  } catch (err) {
    console.error('[roundSync] Failed to create round:', err);
    return null;
  }
}

export function syncHoleScores(
  roundId: string,
  holeNumber: number,
  gameState: GameState,
  commissionerUserId?: string,
): void {
  const allPlayers = [
    ...(gameState.teams[0]?.players ?? []),
    ...(gameState.teams[1]?.players ?? []),
  ];

  const holeScores = allPlayers
    .map((player) => {
      const score = gameState.scores.find(
        (s) => s.playerId === player.id && s.holeNumber === holeNumber
      );
      if (!score || score.grossScore === null) return null;
      return {
        participantId: player.id,
        grossScore: score.grossScore,
        netScore: score.netScore ?? score.grossScore,
        receivesStroke: score.receivesStroke,
      };
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);

  if (holeScores.length === 0) return;

  enqueueSync({
    type: 'hole_scores',
    roundId,
    holeNumber,
    scores: holeScores,
  }, commissionerUserId);
}

export function syncPress(
  roundId: string,
  betType: string,
  side: 'top' | 'bottom',
  startingHole: number,
  stackCount: number = 1,
  parentPressId?: string,
  clientId?: string,
  commissionerUserId?: string,
): void {
  enqueueSync({
    type: 'press',
    roundId,
    betType,
    initiatedBy: side,
    startingHole,
    stackCount,
    parentPressId,
    clientId,
  }, commissionerUserId);
}

export function syncClearHoles(
  roundId: string,
  holeNumbers: number[],
  commissionerUserId?: string,
): void {
  if (holeNumbers.length === 0) return;
  enqueueSync({
    type: 'clear_holes',
    roundId,
    holeNumbers,
  }, commissionerUserId);
}

export function syncDeletePress(
  roundId: string,
  pressId: string,
  commissionerUserId?: string,
): void {
  enqueueSync({
    type: 'delete_press',
    roundId,
    pressId,
  }, commissionerUserId);
}

export function syncActiveHole(
  roundId: string,
  holeNumber: number,
  commissionerUserId?: string,
): void {
  enqueueSync({
    type: 'active_hole',
    roundId,
    holeNumber,
  }, commissionerUserId);
}

export async function finalizeRoundOnServer(
  roundId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    await apiRequest('POST', `/api/rounds/${roundId}/finalize`, {});
    return { ok: true };
  } catch (err: any) {
    const msg =
      err?.response?.data?.error ||
      err?.message ||
      'Failed to finalize round. Please check your connection and try again.';
    console.error('[roundSync] Failed to finalize round:', msg);
    return { ok: false, error: msg };
  }
}

export async function unfinalizeRoundOnServer(
  roundId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    await apiRequest('POST', `/api/rounds/${roundId}/unfinalize`, {});
    return { ok: true };
  } catch (e: any) {
    const errorMsg = e?.response?.data?.error || e?.message || 'Could not reopen this round for editing.';
    console.error('[unfinalizeRoundOnServer] failed', errorMsg);
    return { ok: false, error: errorMsg };
  }
}

export async function syncRoundSetup(
  roundId: string,
  gameState: GameState,
): Promise<boolean> {
  const payload = {
    teamsJson: gameState.teams,
    courseSnapshotJson: { holes: gameState.holes },
    matchFormat: gameState.matchFormat,
    gameMode: gameState.gameMode,
    startingHole: gameState.startingHole,
    stakeFront: gameState.stakes.front,
    stakeBack: gameState.stakes.back,
    stakeOverall: gameState.stakes.overall,
  };
  try {
    const res = await apiRequest('PATCH', `/api/rounds/${roundId}/setup`, payload);
    return res.ok;
  } catch (err) {
    console.error('[roundSync] syncRoundSetup failed:', err);
    return false;
  }
}
